import type { InvoiceChangeLogEntry } from '@erp/rpc';

/**
 * A write's reason, carried as the final `reason:<text>` group of the audit
 * `source_detail` (e.g. by the Update AP Invoice Field workflow step).
 */
const AUDIT_REASON_MARKER = /(?:^|\s)reason:/;

function splitAuditReason(detail: string): {
  detail: string;
  reason: string | null;
} {
  const match = AUDIT_REASON_MARKER.exec(detail);
  if (!match) return { detail, reason: null };
  const reason = detail.slice(match.index + match[0].length).trim();
  return { detail: detail.slice(0, match.index), reason: reason || null };
}

/** The reason recorded with a change, if any. */
export function auditReason(
  actor: InvoiceChangeLogEntry['actor'],
): string | null {
  return splitAuditReason(actor.sourceDetail ?? '').reason;
}

/**
 * Who made the change. For workflow writes the audit `source_detail` is
 * `workflow_run:<id> step:<stepType> [agent/other suffixes] [reason:<text>]`;
 * the reason is shown separately (see {@link auditReason}). The run id is
 * resolved to a name server-side (`entry.workflowName`), so here we name the
 * workflow and the step, falling back to the raw step type when the run is
 * unknown (e.g. deleted) and to a bare "Workflow" for legacy rows.
 */
export function describeActor(
  actor: InvoiceChangeLogEntry['actor'],
  workflowName?: string | null,
): string {
  if (actor.userName) return actor.userName;
  if (actor.sourceType === 'workflow') {
    const { detail } = splitAuditReason(actor.sourceDetail ?? '');
    const match = detail.match(/step:([^\s]+)(.*)/);
    const step = match ? match[1].replace(/_/g, ' ') : null;
    const flags = match ? match[2].trim() : '';
    const subject = workflowName ?? 'Workflow';
    const base = step ? `${subject}: ${step}` : subject;
    return flags ? `${base} (${flags})` : base;
  }
  if (actor.sourceType === 'system') return 'System';
  if (actor.sourceType === 'api') return 'API';
  return 'Unknown';
}
